/**
 * Ark UI Select for notification sound preset (Account settings).
 * Styled for dark/light theme via CSS variables (not native `<select>`).
 */

import { useMemo } from 'react';
import type { BuiltinNotificationSoundId } from '../constants/builtinNotificationSounds';
import type { NotificationSoundId } from '../constants/notificationSoundPreferenceShared';
import { SettingsSelect } from './SettingsSelect';

export interface NotificationSoundSelectLabels {
  none: string;
  custom: string;
}

export interface NotificationSoundSelectProps {
  value: NotificationSoundId;
  disabled?: boolean;
  hasCustomSoundPicker: boolean;
  /** Built-in presets in display order (from `BUILTIN_NOTIFICATION_SOUNDS`). */
  builtinItems: { value: BuiltinNotificationSoundId; label: string }[];
  labels: NotificationSoundSelectLabels;
  onValueChange: (id: NotificationSoundId) => void;
  /**
   * id of an element that names this control (passed as `aria-labelledby`).
   * Do not set `id` on Select.Trigger — Ark/Zag resolves the anchor for positioning by
   * internal id; overriding it breaks the popper and pins the menu to the top-left.
   */
  labelId?: string;
}

export function NotificationSoundSelect({
  value,
  disabled = false,
  hasCustomSoundPicker,
  builtinItems,
  labels,
  onValueChange,
  labelId,
}: NotificationSoundSelectProps) {
  const items = useMemo(() => {
    const base = [
      ...builtinItems.map((item) => ({ value: item.value, label: item.label })),
      { value: 'none', label: labels.none },
    ];
    if (hasCustomSoundPicker) {
      base.push({ value: 'custom', label: labels.custom });
    }
    return base;
  }, [builtinItems, hasCustomSoundPicker, labels]);

  return (
    <SettingsSelect
      value={value}
      items={items}
      disabled={disabled}
      labelId={labelId}
      onValueChange={(next) => onValueChange(next as NotificationSoundId)}
    />
  );
}
